<template>
  <b-container>
    <b-card-actions no-actions>
      <b-row>
        <b-col>
          <div>
            <b-row
              v-if="!isChoppGiftCardConversion"
              class="method-buttons mb-1"
            >
              <b-col md="12">
                <p class="h4">
                  {{ $t('Formas de pagamento') }}
                </p>
              </b-col>
              <b-col
                v-for="item in regularPaymentMethodsInPdv"
                :key="item.id"
                md="4"
              >
                <e-button-card
                  :id="`tab_payment-method_btn-${item.id}`"
                  icon-component="BIcon"
                  :icon="getIconByPaymentMethod[item.method]"
                  :text-title="item.name"
                  :text-shortcuts="
                    getShortcutByPaymentMethod[item.method]
                      ? [getShortcutByPaymentMethod[item.method]]
                      : []
                  "
                  @click="onShowModalValue(item)"
                />
              </b-col>
              <!-- Clube echope: exibido quando cliente tem saldo, há forma de pagamento marcada como IsEchopeClub e não é Kiosk -->
              <b-col
                v-if="!isKioskStore && hasEchopeClubBalance && echopeClubPaymentMethod"
                md="4"
              >
                <e-button-card
                  :id="`tab_payment-method_btn-${echopeClubPaymentMethod.id}`"
                  icon-component="BIcon"
                  icon="star-fill"
                  :text-title="`${echopeClubPaymentMethod.name} (${$options.filters.currency(echopeClubBalanceValue)})`"
                  :btn-attr="{ variant: 'warning' }"
                  @click="onShowEchopeClubPayment"
                />
              </b-col>
            </b-row>

            <b-row class="border-top-dark py-1">
              <b-col>
                <p class="h2 title">
                  Recebimentos
                </p>
              </b-col>
              <b-col v-if="items.length > 0">
                <p class="h3 title text-right">
                  <template v-if="paymentForm.balance > 0">
                    Saldo a pagar: <br>
                    {{ paymentForm.balance | currency }}
                  </template>
                  <template v-else>
                    Troco: <br>
                    {{ paymentForm.change | currency }}
                  </template>
                </p>
              </b-col>
            </b-row>

            <b-row v-if="hasGenericTefTransactionFailed">
              <b-col>
                <b-alert
                  show
                  variant="danger"
                  class="p-2"
                >
                  {{ $t('PAY_BOX_SALE.TEF.ERROR_TRANSACTION_FAILED') }}
                  <br>
                  <br>
                  {{ $t('PAY_BOX_SALE.TEF.SALE_UUID_IDENTIFIER', { uuid: currentSaleUuid }) }}
                </b-alert>
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <method-line
                  v-for="(receipt, index) in paymentForm.receipts"
                  :id="`tab_payment-method_item-${index}`"
                  :key="index"
                  :receipt="receipt"
                  :value="receipt.value"
                  :is-cancelled="receipt.canceled"
                  :show-btn-canceled="isToShowCancelBtn(receipt)"
                  @click-delete="onDeleteMethodPayment(index)"
                  @click-print="stPrintReceipt({ index })"
                  @click-check-payment="onCheckPaymentStatus(index)"
                  @click-confirm-payment="onShowPaymentConfirm(receipt)"
                />
              </b-col>
            </b-row>

            <b-row v-if="hasCanceledTefPixPayment">
              <b-col>
                <b-alert
                  show
                  variant="danger"
                  class="p-2"
                >
                  {{ $t('PAY_BOX_SALE.TEF.INSTRUCTION_TEF_CANCEL_PIX') }}
                </b-alert>
              </b-col>
            </b-row>

            <b-row v-if="hasActiveTefCardPayment">
              <b-col>
                <b-alert
                  show
                  variant="danger"
                  class="p-2"
                >
                  {{ $t('PAY_BOX_SALE.TEF.INSTRUCTION_TEF_CANCEL') }}
                </b-alert>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </b-card-actions>

    <modal-value
      ref="modalValue"
      :sale-uuid="currentSaleUuid"
      :balance-value="paymentForm.balance"
      :btn-confirm-busy="waitPeripheral"
      @click-confirm="onAddMethodPayment"
    />

    <modal-manual-payment-confirm
      ref="modalPaymentConfirm"
      @click-confirm="onManualConfirmPayment"
    />

    <reward-code-verification-modal
      ref="echopeClubCodeVerificationModal"
      :show-virtual-keyboard="false"
      :show-overlay-loading="true"
      @verified="onEchopeClubCodeVerified"
      @cancelled="onEchopeClubCodeCancelled"
    />
  </b-container>
</template>

<script>
import { BContainer, BRow, BCol, BAlert } from 'bootstrap-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { keyboardCode, paymentTypes, loadingOverlay, payBoxTypes, payBoxPayments } from '@/mixins'
import EButtonCard from '@/views/components/EButtonCard.vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'

import { isTefCard, isTefPix } from '@/store/utils/paybox/paybox-payments'
import { localListFindIndex } from '@/store/utils'
import delegablePermissions from '@/utils/delegable-permissions'
import RewardCodeVerificationModal from '@/views/pages/pdv/atm/components/modals/RewardCodeVerificationModal.vue'
import MethodLine from './components/MethodLine.vue'
import ModalValue from './components/ModalValue.vue'
import ModalManualPaymentConfirm from './components/ModalManualPaymentConfirm.vue'

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BCardActions,
    EButtonCard,
    MethodLine,
    BAlert,
    ModalValue,
    ModalManualPaymentConfirm,
    RewardCodeVerificationModal,
  },

  mixins: [keyboardCode, paymentTypes, loadingOverlay, payBoxTypes, payBoxPayments],

  data() {
    return {
      showModalValue: true,
      waitPeripheral: false,
      userDelegatedPixConfirmation: null,
      pendingEchopeClubValue: null,
    }
  },

  computed: {
    ...mapState('app', ['modalAuthRef']),
    ...mapState('pages/pdv', ['paymentMethodsInPdv']),
    ...mapState('pages/pdv/payBox', ['paymentForm', 'items', 'clientForm']),
    ...mapGetters('pages/pdv/payBoxConfiguration', ['tefAgentConfigPayload', 'isKioskStore']),
    ...mapGetters('pages/pdv/payBox', [
      'getSubtotalSale',
      'getTotalSale',
      'isConsignedSaleFinish',
      'isChoppGiftCardConversion',
      'currentSaleUuid',
      'hasActiveTefCardPayment',
      'hasCanceledTefPixPayment',
      'hasActiveTefPixPaymentFailed',
      'hasGenericTefTransactionFailed',
    ]),
    ...mapState('echopeClub', {
      echopeClubBalance: 'echopeClubBalance',
    }),
    ...mapGetters('echopeClub', ['hasEchopeClubBalance', 'echopeClubBalanceValue']),

    // Forma de pagamento marcada como IsEchopeClub na lista carregada do PDV
    echopeClubPaymentMethod() {
      return this.paymentMethodsInPdv.find(pm => pm.isEchopeClub === true) ?? null
    },

    // Lista sem a forma de pagamento do Clube echope (exibida separadamente)
    regularPaymentMethodsInPdv() {
      return this.paymentMethodsInPdv.filter(pm => !pm.isEchopeClub)
    },

    getIconByPaymentMethod() {
      return {
        None: 'currency-dollar',
        CreditCard: 'credit-card',
        DebitCard: 'credit-card',
        Pix: 'x-diamond-fill',
        Cash: 'cash',
      }
    },

    getShortcutByPaymentMethod() {
      return {
        None: undefined,
        CreditCard: this.keyCode.f2,
        DebitCard: this.keyCode.f3,
        Pix: this.keyCode.f4,
        Cash: this.keyCode.f1,
      }
    },
  },

  methods: {
    ...mapActions('pages/pdv/payBox', {
      stAddReceipt: 'addReceipt',
      stConfirmReceipt: 'confirmReceipt',
      stRemoveReceipt: 'removeReceipt',
      stPrintReceipt: 'printReceipt',
      stValidateAddReceipt: 'validateAddReceipt',
    }),

    async onShowModalValue(paymentMethod) {
      if (await this.mxPayBoxActionIsBlocked(this.uiPayBoxActionsEnum.PAYMENT_ADD)) {
        return
      }

      const error = await this.stValidateAddReceipt()

      if (error.message) {
        this.showWarning({ message: this.$t(error.message) })
        return
      }

      this.$refs.modalValue.showModal(paymentMethod)
    },

    async onShowEchopeClubPayment() {
      if (await this.mxPayBoxActionIsBlocked(this.uiPayBoxActionsEnum.PAYMENT_ADD)) {
        return
      }

      const error = await this.stValidateAddReceipt()
      if (error.message) {
        this.showWarning({ message: this.$t(error.message) })
        return
      }

      // O valor a usar é o menor entre o saldo do clube e o saldo a pagar
      const balanceToUse = Math.min(
        this.echopeClubBalanceValue,
        this.paymentForm.balance
      )

      // Guarda o valor calculado e abre o modal de verificação de código do cliente
      this.pendingEchopeClubValue = balanceToUse
      await this.$refs.echopeClubCodeVerificationModal.show(
        this.clientForm.id,
        balanceToUse
      )
    },

    async onEchopeClubCodeVerified() {
      try {
        this.waitPeripheral = true

        const receipt = {
          paymentMethod: {
            id: this.echopeClubPaymentMethod.id,
            name: this.echopeClubPaymentMethod.name,
            method: this.echopeClubPaymentMethod.method,
          },
          paymentMethodInterface: 'Manual',
          value: this.pendingEchopeClubValue,
          canceled: false,
          change: 0,
        }

        await this.stAddReceipt(receipt)
        await this.stPrintReceipt({ index: -1 })
      } catch (receiptError) {
        this.showError({
          error: receiptError,
          message: this.$t('Erro ao adicionar pagamento Clube echope'),
        })
      } finally {
        this.pendingEchopeClubValue = null
        this.waitPeripheral = false
      }
    },

    onEchopeClubCodeCancelled() {
      this.pendingEchopeClubValue = null
    },

    async onShowPaymentConfirm(receipt) {
      this.userDelegatedPixConfirmation = await this.modalAuthRef.auth(
        delegablePermissions.PDV_PIX_MANUAL_CONFIRMATION
      )

      this.$refs.modalPaymentConfirm.showModal(receipt)
    },

    isToShowCancelBtn(item) {
      if (item.id) return false
      return true
    },

    async onAddMethodPayment(value) {
      try {
        this.waitPeripheral = true

        await this.stAddReceipt(value)

        if (!value.tefTransactionFailed) {
          await this.stPrintReceipt({ index: -1 }) // print last
        }

        this.$refs.modalValue.hideModal()
      } catch (error) {
        this.showError({ error, message: 'Erro ao adicionar pagamento' })
      } finally {
        this.waitPeripheral = false
      }

      if (this.mxIsToCheckPixStatus(value)) {
        await this.onCheckPaymentStatus(this.paymentForm.receipts.length - 1)
      }
    },

    async onManualConfirmPayment(receipt, confirmData) {
      try {
        this.waitPeripheral = true

        this.stConfirmReceipt({
          receipt,
          confirmData: {
            ...confirmData,
            confirmationActionType: this.paymentActionTypeEnum.MANUAL,
          },
          userDelegated: this.userDelegatedPixConfirmation,
        })

        const receiptIndex = localListFindIndex(this.paymentForm.receipts, receipt)
        await this.stPrintReceipt({ index: receiptIndex })
      } catch (error) {
        this.showError({ error, message: 'Erro ao confirmar pagamento' })
      } finally {
        this.waitPeripheral = false
      }
    },

    async onDeleteMethodPayment(index) {
      try {
        if (await this.mxPayBoxActionIsBlocked(this.uiPayBoxActionsEnum.PAYMENT_REMOVE)) {
          return
        }

        const confirmOptions = {
          text: this.$t('Deseja realmente cancelar este recebimento ?'),
          focusCancel: true,
        }

        const receipt = this.paymentForm.receipts[index]

        if (isTefPix(receipt)) {
          confirmOptions.text = this.$t('PAY_BOX_SALE.TEF.CONFIRMATION_TEF_CANCEL_PIX')
        }

        const confirm = await this.confirm(confirmOptions)
        if (confirm) {
          const tefPaymentCancelation = await this.cancelTefPayment(receipt)
          if (tefPaymentCancelation || !isTefCard(receipt)) {
            this.stRemoveReceipt({
              index,
              tefPaymentCancelation,
              cancelationActionType: tefPaymentCancelation
                ? this.paymentActionTypeEnum.PIN_PAD
                : this.paymentActionTypeEnum.MANUAL,
            })
          }
        }
      } catch (error) {
        this.showError({ error })
      }
    },

    async onCheckPaymentStatus(index) {
      try {
        const receipt = this.paymentForm.receipts[index]

        this.waitPeripheral = true

        const tefResult = await this.pixCheckStatus(receipt, this.currentSaleUuid)

        if (!tefResult) {
          return
        }

        if (tefResult?.transactionFailed) {
          this.showWarning({
            message: this.$t('PAY_BOX_SALE.TEF.PIX_CHECK_STATUS_TRANSACTION_FAILED'),
          })
          return
        }

        if (tefResult?.paymentApproved) {
          await this.stConfirmReceipt({
            receipt,
            confirmData: {
              tefPayment: tefResult,
              confirmationActionType: this.paymentActionTypeEnum.PIN_PAD,
            },
          })

          await this.stPrintReceipt({ index })
        } else {
          this.showWarning({ message: this.$t('Pagamento não foi aprovado pela adquirente.') })
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.waitPeripheral = false
      }
    },

    paymentShortcut(e) {
      const filterPayment = p => this.paymentMethodsInPdv.filter(m => m.method === p)[0]
      const actionsF = {
        [this.keyCode.f1]: () => this.onShowModalValue(filterPayment(this.paymentTypeEnum.CASH)),
        [this.keyCode.f2]: () =>
          this.onShowModalValue(filterPayment(this.paymentTypeEnum.CREDIT_CARD)),
        [this.keyCode.f3]: () =>
          this.onShowModalValue(filterPayment(this.paymentTypeEnum.DEBIT_CARD)),
        [this.keyCode.f4]: () => this.onShowModalValue(filterPayment(this.paymentTypeEnum.PIX)),
      }

      if (actionsF[e.code]) {
        e.preventDefault()
        actionsF[e.code]()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.title {
  font-weight: 600;
}

.method-buttons {
  row-gap: 10px;
}
</style>
